import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function useUrlState(keys) {
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => Object.fromEntries(keys.map((k) => [k, sp.getAll(k)])), [sp, keys]);
  const update = useCallback(
    (mutate) =>
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          next.delete("page");
          return next;
        },
        { replace: true },
      ),
    [setSp],
  );
  return {
    sp,
    filters,
    q: sp.get("q") || "",
    page: Math.max(1, Number(sp.get("page") || 1)),
    setValues: (key, values) => update((n) => { n.delete(key); values.forEach((v) => n.append(key, v)); }),
    setParam: (key, value) => update((n) => (value ? n.set(key, value) : n.delete(key))),
    removeValue: (key, value) => update((n) => { const rest = n.getAll(key).filter((v) => v !== value); n.delete(key); rest.forEach((v) => n.append(key, v)); }),
    setPage: (page) => setSp((prev) => { const n = new URLSearchParams(prev); page > 1 ? n.set("page", page) : n.delete("page"); return n; }),
    clearAll: () => setSp(new URLSearchParams(), { replace: true }),
  };
}

export function usePersistentView(storageKey) {
  const [view, setView] = useState(() => localStorage.getItem(storageKey) || "cards");
  const change = (value) => {
    if (!value) return;
    localStorage.setItem(storageKey, value);
    setView(value);
  };
  return [view, change];
}

export function useGroups() {
  return useQuery({ queryKey: ["groups"], queryFn: async () => (await api.get("/groups")).data, staleTime: 60_000 });
}

export function useOptions(enabled = true) {
  return useQuery({ queryKey: ["options"], queryFn: async () => (await api.get("/options")).data, staleTime: 300_000, enabled });
}

export function useExpertises() {
  return useQuery({ queryKey: ["expertises"], queryFn: async () => (await api.get("/expertises")).data, staleTime: 60_000 });
}

export function useGroupIndex() {
  const { data } = useGroups();
  return useMemo(() => {
    const index = {};
    Object.values(data || {}).flat().forEach((g) => { index[g.id] = g; });
    return index;
  }, [data]);
}

export function useOrgRefs(ids) {
  return useQuery({
    queryKey: ["org-refs", ids],
    queryFn: async () => (await api.get(`/organizations/refs?${ids.map((i) => `ids=${i}`).join("&")}`)).data,
    enabled: ids.length > 0,
    staleTime: 60_000,
  });
}

export function useProgrammations(enabled = true) {
  return useQuery({
    queryKey: ["programmations"],
    queryFn: async () => (await api.get("/activity/programmation")).data,
    staleTime: 300_000,
    enabled,
  });
}

export function useEvents(enabled = true) {
  return useQuery({
    queryKey: ["events"],
    queryFn: async () => (await api.get("/activity/events")).data,
    staleTime: 300_000,
    enabled,
  });
}

